/**
 * Automatic fact check after writing from research (2026-10-01).
 *
 * The research page's "Fact check after writing" box sends
 * `auto_fact_check: true` with each write. The backend marks the
 * article `auto_fact_check: {status: "running"}`, flips the write task
 * to ready (so the queue starts writing the NEXT article), and then
 * fact-checks this one. The write queue polls the article until the
 * state is "done" or "error" (backend/auto_fact_check.py has the shape).
 *
 * Pure and framework-free — node --test frontend/scripts/autoFactCheck.test.mjs
 */

export const AUTO_CHECK_PREF_KEY = "newsdesk.autoFactCheck";

// How often the queue re-reads an article while its check runs, and
// when it gives up. A check with web search takes ~1 minute; a server
// restart mid-check leaves the state "running" forever, so a check
// older than STALE_AFTER_MS is reported as interrupted.
export const POLL_MS = 6000;
export const STALE_AFTER_MS = 15 * 60 * 1000;

/** Per-browser memory of the checkbox. ON by default (2026-10-04): it
 *  is off only when the editor unticked it in this browser, and it then
 *  stays off until they tick it again. Storage can be unavailable
 *  (private mode, blocked site data) — that also reads as on. */
export function readAutoCheckPref(storage) {
  try {
    return (storage || globalThis.localStorage)?.getItem(AUTO_CHECK_PREF_KEY) !== "0";
  } catch {
    return true;
  }
}

export function writeAutoCheckPref(value, storage) {
  try {
    (storage || globalThis.localStorage)?.setItem(AUTO_CHECK_PREF_KEY, value ? "1" : "0");
  } catch {
    /* best effort */
  }
}

/**
 * Normalise the article's `auto_fact_check` for the queue.
 * Returns null when there is no state yet (treated as still running by
 * the poller), otherwise a copy with `status` one of
 * "running" | "done" | "error". A "running" state older than
 * STALE_AFTER_MS becomes an "error" (the server was restarted mid-check).
 */
export function checkState(raw, nowMs = Date.now()) {
  if (!raw || typeof raw !== "object") return null;
  const status = raw.status;
  if (status === "done" || status === "error") return { ...raw };
  if (status === "running") {
    const started = Date.parse(raw.started_at || "");
    if (Number.isFinite(started) && nowMs - started > STALE_AFTER_MS) {
      return {
        status: "error",
        error: "The fact check was interrupted — run it again from the article page.",
      };
    }
    return { ...raw };
  }
  return null;
}

export const isTerminal = (st) => !!st && (st.status === "done" || st.status === "error");

/** One-line result: "82/100 · 2 to review · 1 fix", "Checking…", or the error. */
export function checkLabel(st) {
  if (!st) return "";
  if (st.status === "running") return "Fact-checking…";
  if (st.status === "error") return st.error || "Fact check failed";
  const parts = [`${Number(st.score) || 0}/100${st.score_estimated ? " (est.)" : ""}`];
  const flagged = Number(st.flagged) || 0;
  parts.push(flagged ? `${flagged} to review` : "nothing flagged");
  const fixes = Number(st.fixes) || 0;
  if (fixes) parts.push(`${fixes} fix${fixes === 1 ? "" : "es"}`);
  return parts.join(" · ");
}

/** "good" | "warn" | "bad" — colours the result row like the score badge. */
export function checkTone(st) {
  if (!st || st.status === "running") return "neutral";
  if (st.status === "error") return "bad";
  const s = Number(st.score) || 0;
  if (s >= 85) return "good";
  if (s >= 70) return "warn";
  return "bad";
}

/** Counts across the batch's items (`item.check` is a checkState). */
export function checkCounts(items) {
  const out = { total: 0, running: 0, done: 0, error: 0 };
  for (const it of items || []) {
    if (!it || !it.check) continue;
    out.total += 1;
    if (it.check.status === "running") out.running += 1;
    else if (it.check.status === "done") out.done += 1;
    else if (it.check.status === "error") out.error += 1;
  }
  return out;
}
